// Acesso a dados dos fluxos — Fases 10 e 11.
//
// Três regras herdadas do modelo da Fase 9, e as três já estão escritas no SQL
// da migration — repetidas aqui porque é o front que as encosta primeiro:
//
//  1. NÓS E ARESTAS PERTENCEM À VERSÃO. Salvar o rascunho é reescrever os nós
//     daquela versão, nunca "do fluxo". É o que faz publicar versão nova não
//     mexer em run andando.
//  2. SÓ RASCUNHO É EDITÁVEL. Versão publicada é histórico; para mudar, cria-se
//     um rascunho novo a partir dela.
//  3. PUBLICAR É `inbox_publicar_versao`, não um UPDATE. A função arquiva a
//     anterior e publica a nova na mesma transação — em dois updates separados
//     existe um instante sem versão publicada, e um run nascendo ali não acha
//     grafo.

import { supabase } from '../../../src/lib/supabase';
import type { ArestaFluxo, Fluxo, Grafo, NoFluxo, PassoRun, Run, Versao } from './tipos';

export async function carregarFluxos(orgId: string): Promise<Fluxo[]> {
  const { data, error } = await supabase
    .from('inbox_flows')
    .select('id, org_id, nome, descricao, ativo, created_at, updated_at')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as Fluxo[];
}

export async function carregarFluxo(flowId: string): Promise<Fluxo | null> {
  const { data, error } = await supabase
    .from('inbox_flows')
    .select('id, org_id, nome, descricao, ativo, created_at, updated_at')
    .eq('id', flowId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as Fluxo) ?? null;
}

export async function criarFluxo(orgId: string, nome: string, descricao: string | null): Promise<Fluxo> {
  const { data, error } = await supabase
    .from('inbox_flows')
    .insert({ org_id: orgId, nome, descricao })
    .select('id, org_id, nome, descricao, ativo, created_at, updated_at')
    .single();
  // A unique é (org_id, nome): dois fluxos com o mesmo nome viram dois cartões
  // idênticos na lista, e ninguém sabe qual está no ar.
  if (error) {
    throw new Error(
      error.code === '23505' ? 'Já existe um fluxo com esse nome.' : error.message,
    );
  }
  return data as Fluxo;
}

export async function renomearFluxo(flowId: string, nome: string, descricao: string | null): Promise<void> {
  const { error } = await supabase
    .from('inbox_flows')
    .update({ nome, descricao, updated_at: new Date().toISOString() })
    .eq('id', flowId);
  if (error) throw new Error(error.code === '23505' ? 'Já existe um fluxo com esse nome.' : error.message);
}

/**
 * Liga e desliga o fluxo.
 *
 * Desligar NÃO encerra os runs em andamento — isso é `inbox_parar_runs`, de
 * propósito separada. Desligar e cancelar são decisões diferentes: a primeira
 * impede novos disparos, a segunda abandona quem já está no meio da conversa.
 */
export async function ligarFluxo(flowId: string, ativo: boolean): Promise<void> {
  const { error } = await supabase
    .from('inbox_flows')
    .update({ ativo, updated_at: new Date().toISOString() })
    .eq('id', flowId);
  if (error) throw new Error(error.message);
}

export async function apagarFluxo(flowId: string): Promise<void> {
  const { error } = await supabase.from('inbox_flows').delete().eq('id', flowId);
  // Run antigo aponta para a versão com `on delete restrict`. Apagar um fluxo
  // que já rodou é recusado pelo banco, e a mensagem crua não diz isso.
  if (error) {
    throw new Error(
      error.code === '23503'
        ? 'Este fluxo já rodou em conversas e por isso não pode ser apagado. Desative-o.'
        : error.message,
    );
  }
}

export async function carregarVersoes(flowId: string): Promise<Versao[]> {
  const { data, error } = await supabase
    .from('inbox_flow_versions')
    .select('id, flow_id, numero, status, no_inicial, published_at, created_at')
    .eq('flow_id', flowId)
    .order('numero', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as Versao[];
}

export async function carregarGrafo(versionId: string): Promise<Grafo> {
  const [nos, arestas, versao] = await Promise.all([
    supabase
      .from('inbox_flow_nodes')
      .select('id, version_id, node_key, tipo, config, posicao')
      .eq('version_id', versionId)
      .order('created_at', { ascending: true }),
    supabase
      .from('inbox_flow_edges')
      .select('id, version_id, de, para, quando')
      .eq('version_id', versionId),
    supabase.from('inbox_flow_versions').select('no_inicial').eq('id', versionId).maybeSingle(),
  ]);

  if (nos.error) throw new Error(nos.error.message);
  if (arestas.error) throw new Error(arestas.error.message);

  return {
    nos: (nos.data ?? []) as NoFluxo[],
    arestas: (arestas.data ?? []) as ArestaFluxo[],
    noInicial: (versao.data?.no_inicial as string | null) ?? null,
  };
}

/** O rascunho aberto do fluxo, se houver. */
export async function rascunhoAberto(flowId: string): Promise<Versao | null> {
  const { data, error } = await supabase
    .from('inbox_flow_versions')
    .select('id, flow_id, numero, status, no_inicial, published_at, created_at')
    .eq('flow_id', flowId)
    .eq('status', 'rascunho')
    .order('numero', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as Versao) ?? null;
}

/**
 * Cria um rascunho novo, copiando o grafo de uma versão existente.
 *
 * É assim que se edita um fluxo publicado: nunca por cima. A publicada continua
 * intocada até o rascunho ser publicado no lugar dela — e os runs que estão
 * andando seguem lendo a versão com que nasceram.
 */
export async function novoRascunho(flowId: string, copiarDe?: Versao | null): Promise<Versao> {
  const versoes = await carregarVersoes(flowId);
  const proximo = versoes.reduce((maior, v) => Math.max(maior, v.numero), 0) + 1;

  const { data, error } = await supabase
    .from('inbox_flow_versions')
    .insert({
      flow_id: flowId,
      numero: proximo,
      status: 'rascunho',
      no_inicial: copiarDe?.no_inicial ?? null,
    })
    .select('id, flow_id, numero, status, no_inicial, published_at, created_at')
    .single();
  if (error) throw new Error(error.message);
  const nova = data as Versao;

  if (copiarDe) {
    const origem = await carregarGrafo(copiarDe.id);
    await salvarGrafo(nova.id, origem);
  }

  return nova;
}

/**
 * Grava o rascunho inteiro: apaga o que havia e reescreve.
 *
 * Parece grosseiro e é o certo aqui. O grafo é pequeno (dezenas de linhas), e
 * um diff incremental teria de acertar renomeação de passo, aresta órfã e
 * ordem de escrita — três chances de deixar o rascunho num estado que o
 * validador aprova e o motor não executa. Reescrever é uma operação só.
 *
 * ⚠️ Só vale para RASCUNHO. Chamar isto com o id de uma versão publicada
 * reescreveria o grafo debaixo dos runs que estão nela.
 */
export async function salvarGrafo(versionId: string, grafo: Grafo): Promise<void> {
  const { data: versao, error: erroVersao } = await supabase
    .from('inbox_flow_versions')
    .select('status')
    .eq('id', versionId)
    .maybeSingle();
  if (erroVersao) throw new Error(erroVersao.message);
  if (!versao) throw new Error('Versão não encontrada.');
  if (versao.status !== 'rascunho') {
    throw new Error('Só rascunho pode ser editado. Crie uma nova versão a partir desta.');
  }

  // Arestas primeiro: elas referenciam os nós por nome, e apagar nó com aresta
  // apontando deixaria um instante inconsistente se algo falhasse no meio.
  const apagaArestas = await supabase.from('inbox_flow_edges').delete().eq('version_id', versionId);
  if (apagaArestas.error) throw new Error(apagaArestas.error.message);
  const apagaNos = await supabase.from('inbox_flow_nodes').delete().eq('version_id', versionId);
  if (apagaNos.error) throw new Error(apagaNos.error.message);

  if (grafo.nos.length > 0) {
    const { error } = await supabase.from('inbox_flow_nodes').insert(
      grafo.nos.map((n) => ({
        version_id: versionId,
        node_key: n.node_key,
        tipo: n.tipo,
        config: n.config ?? {},
        posicao: n.posicao ?? { x: 0, y: 0 },
      })),
    );
    if (error) throw new Error(error.message);
  }

  if (grafo.arestas.length > 0) {
    const { error } = await supabase.from('inbox_flow_edges').insert(
      grafo.arestas.map((a) => ({
        version_id: versionId,
        de: a.de,
        para: a.para,
        quando: a.quando,
      })),
    );
    if (error) throw new Error(error.message);
  }

  const { error: erroInicial } = await supabase
    .from('inbox_flow_versions')
    .update({ no_inicial: grafo.noInicial })
    .eq('id', versionId);
  if (erroInicial) throw new Error(erroInicial.message);
}

/**
 * Publica o rascunho.
 *
 * A função do banco devolve `{ ok, erro }` em vez de lançar: os motivos de
 * recusa são de negócio ("já existe versão publicada", "sem passo inicial") e
 * precisam chegar legíveis na tela, não como exceção de SQL.
 */
export async function publicarVersao(versionId: string): Promise<void> {
  const { data, error } = await supabase.rpc('inbox_publicar_versao', { p_version_id: versionId });
  if (error) throw new Error(error.message);
  const r = data as { ok: boolean; erro?: string };
  if (!r?.ok) throw new Error(r?.erro ?? 'Não foi possível publicar esta versão.');
}

// ─── Execução (leitura) ─────────────────────────────────────────────────────
//
// Só leitura, e é assim no banco também: a RLS de `inbox_flow_runs` não tem
// policy de escrita para `authenticated`. Avançar um run pela API seria pular
// as quatro travas da Fase 9.

export async function carregarRuns(flowId: string, limite = 50): Promise<Run[]> {
  const { data, error } = await supabase
    .from('inbox_flow_runs')
    .select('id, flow_id, version_id, conversation_id, current_node, status, mensagens_enviadas, retomar_em, motivo_parada, created_at, updated_at')
    .eq('flow_id', flowId)
    .order('updated_at', { ascending: false })
    .limit(limite);
  if (error) throw new Error(error.message);
  return (data ?? []) as Run[];
}

export async function carregarPassos(runId: string): Promise<PassoRun[]> {
  const { data, error } = await supabase
    .from('inbox_flow_run_steps')
    .select('id, run_id, node_key, registro, evento, created_at')
    .eq('run_id', runId)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as PassoRun[];
}

/** Etiquetas da org, para o passo "aplicar etiqueta". */
export async function carregarTags(orgId: string): Promise<{ id: string; nome: string; cor: string }[]> {
  const { data, error } = await supabase
    .from('inbox_tags')
    .select('id, nome, cor')
    .eq('org_id', orgId)
    .order('nome');
  if (error) throw new Error(error.message);
  return (data ?? []) as { id: string; nome: string; cor: string }[];
}

/** Membros da org, para o passo "atribuir responsável". */
export async function carregarMembros(orgId: string): Promise<{ id: string; nome: string }[]> {
  const { data, error } = await supabase
    .from('inbox_members')
    .select('user_id, profiles:profiles!inbox_members_user_id_fkey(id, full_name)')
    .eq('org_id', orgId);
  if (error) throw new Error(error.message);
  return ((data ?? []) as any[])
    .map((m) => ({ id: String(m.user_id), nome: m.profiles?.full_name ?? 'Sem nome' }))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}
